import React, { useEffect } from "react"
import { withRouter } from "react-router-dom"
import { connect } from "react-redux"
import ApplicationLayout from "../../layouts/ApplicationLayout"
import ReactSVG from "react-svg"
import {
  AUTO_GLASS_REPAIR,
  AUTO_GLASS_REPLACEMENT,
  ROADSIDE_ASSISTANCE_URL,
  TELEMATICS_URL
} from "../../constants/common"
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ZUBIE, FLEETCOR, DISCOUNT_TIRE } from "../../constants/application"
import { getSubdomain } from "../../components/shared/StylesheetInjector"
import infoOutlined from "../../assets/images/info-outlined-icon.svg"
import { routerPaths } from "../../constants/paths"
import { gaJSON, trackFirebaseAnalytics } from "../../helpers/googleAnalyticsHelpers"
import SplitClient, { TIRES_FLEET, VALVOLINE_BANNER } from "../../components/config/SplitClient"
import { TIRES_RESTRICTED_ROLES } from "../../constants/roles"

export const FeatureCard = ({
  icon,
  title,
  description,
  linkText,
  link,
  linkClick,
  iconSize,
  titleSize
}) => (
  <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow flex flex-col justify-between gap-5">
    <div className="bg-neutral-100 p-3 flex items-center justify-center mx-auto rounded w-full">
      <div className={`${iconSize ? iconSize : "size-40"} flex items-center justify-center w-full`}>
        {icon}
      </div>
    </div>
    <h3 className={`${titleSize ? titleSize : "text-xl font-semibold"} text-gray-800 m-0`}>
      {title}
    </h3>
    <p className="text-gray-600 m-0">{description}</p>
    <div className="flex items-center justify-between">
      {link && (
        <a
          href={link}
          className="link hover:text-red-700 font-medium underline"
          onClick={linkClick}
          target="_blank"
        >
          {linkText}
        </a>
      )}
      {linkClick && (
        <p className="link hover:text-red-700 font-medium underline" onClick={linkClick}>
          {linkText}
        </p>
      )}
    </div>
  </div>
)

const Offers = ({ history, currentUser }) => {
  const [showAutoGlass, setShowAutoGlass] = React.useState(false)
  const [showTires, setShowTires] = React.useState(false)

  useEffect(() => {
    if (showAutoGlass) {
      window.history.pushState({ autoGlassOpen: true }, "", "")
    }
    const handlePopState = (event) => {
      if (showAutoGlass) {
        setShowAutoGlass(false)
      }
    }
    window.addEventListener("popstate", handlePopState)
    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  }, [showAutoGlass])

  useEffect(() => {
    async function getFlag() {
      const result = await SplitClient.getTreatment(TIRES_FLEET, currentUser, {
        sub_domain: getSubdomain(),
        app_name: getSubdomain()
      })
      setShowTires(result[TIRES_FLEET] === "on")
    }
    getFlag()
  }, [currentUser])

  const isShowTires = () => {
    const roles = currentUser?.roles || []
    const hasAllowedRole = roles.some((role) => !TIRES_RESTRICTED_ROLES.includes(role))
    return getSubdomain() !== DISCOUNT_TIRE && hasAllowedRole && showTires
  }

  return (
    <ApplicationLayout>
      {!showAutoGlass && (
        <div className="max-w-[1500px] mx-auto px-4 py-8">
          <div className="mb-[50px]">
            <h1 className="text-3xl font-bold text-center text-gray-800">Fleet 360&deg;</h1>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-5">
            <div className="px-5">
              <p className="text-md font-semibold !text-black m-0">Save Time</p>
              <p className="text-gray-600">
                Skip the research - we've already identified top solutions in each category.
              </p>
            </div>
            <div className="border-l-1 px-5">
              <p className="text-md font-semibold !text-black m-0">Trusted Partners</p>
              <p className="text-gray-600">We only feature partners we've vetted and trust.</p>
            </div>
            <div className="border-l-1 px-5">
              <p className="text-md font-semibold !text-black m-0">Comprehensive Solution</p>
              <p className="text-gray-600">
                Everything you need to manage your fleet, linked in one place.
              </p>
            </div>
          </div>

          {getSubdomain() === FLEETCOR && (
            <div className="bg-white rounded-lg shadow-md p-2 hover:shadow-lg transition-shadow duration-300 mb-2">
              <div className="bg-neutral-100 p-1 flex mx-auto rounded">
                <img src={infoOutlined} className="padding-right-10" />
                <b class="mr-2 ml-1 !text-black">Heads up:</b> Fuelman cards can't be used for
                payment. You'll be able to enter an alternate payment method when you shop on their
                site.
              </div>
            </div>
          )}

          <div
            className={
              getSubdomain() !== ZUBIE && getSubdomain() !== DISCOUNT_TIRE && isShowTires()
                ? "grid gap-8 md:grid-cols-4"
                : getSubdomain() === ZUBIE && !isShowTires()
                ? "flex gap-5 justify-center"
                : "grid gap-8 md:grid-cols-3 justify-center"
            }
          >
            {getSubdomain() !== DISCOUNT_TIRE && isShowTires() && (
              <div className="space-y-6 flex">
                <FeatureCard
                  icon={<ReactSVG src={`/images/tires.svg`} />}
                  title="Shop New Tires"
                  description="Shop for tires by vehicle, compare pricing, and schedule installation - all in one place. Tires ship to your shop of choice before the appointment."
                  linkText="Shop and schedule >"
                  linkClick={() => {
                    trackFirebaseAnalytics({
                      event: gaJSON.tires_entry_clicked,
                      params: {
                        entry_point: "fleet_360",
                        user_role: currentUser.roles.toString()
                      }
                    })
                    history.push(routerPaths.selectVehicle + "?exit=offers")
                  }}
                />
              </div>
            )}
            {/* Save Time Section */}
            <div className="space-y-6 flex">
              <FeatureCard
                icon={<ReactSVG src={`/images/truck-tow.svg`} />}
                title="Roadside Assistance"
                description="Get prompt help during roadside emergencies with reliable assistance services."
                linkText="Get roadside assistance >"
                link={ROADSIDE_ASSISTANCE_URL}
              />
            </div>

            {/* Trusted Partners Section */}
            <div className="space-y-6 flex">
              <FeatureCard
                icon={<ReactSVG src={`/images/auto-glass.svg`} />}
                title="Auto Glass"
                description="Keep your view clear with professional auto glass repair and replacement solutions."
                linkText="Get auto glass repaired >"
                linkClick={() => setShowAutoGlass(true)}
              />
            </div>

            {/* Comprehensive Solution Section */}
            {getSubdomain() !== ZUBIE && (
              <div className="space-y-6 flex">
                <FeatureCard
                  icon={<ReactSVG src={`/images/telematics.svg`} />}
                  title="Telematics"
                  description="Connect your vehicles for real-time tracking, mileage, fuel level, and more."
                  linkText="Get started >"
                  link={TELEMATICS_URL}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {showAutoGlass && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div
            className="flex items-center gap-5 pb-5 !text-gray-700 cursor-pointer"
            onClick={() => setShowAutoGlass(false)}
          >
            <FontAwesomeIcon icon={faArrowLeft} size="lg" />
            <p className="text-lg !text-gray-700 font-bold">Auto Glass</p>
          </div>
          <div className="grid md:grid-cols-5 gap-8">
            <div className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow duration-300 col-span-2">
              <div className="bg-neutral-100 p-3 flex items-center justify-center mx-auto rounded">
                <div className="size-40 flex items-center justify-center">
                  <ReactSVG src={`/images/safelite.svg`} />
                </div>
              </div>

              <p className="text-2xl font-semibold !text-black !my-[20px] inline-block">Safelite</p>

              <p className="text-gray-600 mb-4">
                Get an instant quote and schedule your auto glass repair online right away.
              </p>

              <p className="text-lg font-semibold !text-black">Benefits:</p>

              <div className="flex flex-col gap-5">
                <div className="flex gap-2 items-center">
                  <ReactSVG src={`/images/tick-black.svg`} />
                  <span>
                    {" "}
                    Over 850 locations across the United States, plus mobile repair that comes to
                    you!
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <ReactSVG src={`/images/tick-black.svg`} />
                  <span>
                    Fix your glass with the best materials and back your service with nationwide
                    warranty.
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <ReactSVG src={`/images/tick-black.svg`} />
                  <span>Safe, no-contact service: Drop & go experience.</span>
                </div>
              </div>
              <div className="mt-[50px] flex flex-col gap-5">
                <button
                  className="offer-button"
                  onClick={() => window.open(AUTO_GLASS_REPLACEMENT, "_blank")}
                >
                  Get a replacement quote
                </button>
                <button
                  className="block w-full py-5 text-center bg-neutral-100 hover:bg-neutral-200 rounded-md text-gray-700 font-bold !text-lg cursor-pointer"
                  onClick={() => window.open(AUTO_GLASS_REPAIR, "_blank")}
                >
                  Get a repair quote
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ApplicationLayout>
  )
}

const mapStateToProps = (state) => {
  const user = state.application.userSessionData || (state.users && state.users.currentUser)

  return {
    currentUser: user
  }
}

export default withRouter(connect(mapStateToProps)(Offers))
